import React from "react";

import type { AnnotationRecord, AnnotationSurfaceKind } from "./annotationSurface.types";

type AnnotationReviewListProps = {
  annotations: AnnotationRecord[];
  prefersLightMode: boolean;
  onSelectAnnotation?: (annotation: AnnotationRecord) => void;
};

const FILTERS: Array<{ key: "all" | AnnotationSurfaceKind; label: string }> = [
  { key: "all", label: "All" },
  { key: "creative", label: "Creative" },
  { key: "continuity", label: "Continuity" },
  { key: "timing", label: "Timing" },
  { key: "audio", label: "Audio" },
  { key: "npc", label: "NPC" },
  { key: "intent", label: "Intent" },
  { key: "publish", label: "Publish" },
];

function typeLabel(kind: AnnotationSurfaceKind): string {
  if (kind === "npc") return "NPC";
  return kind.charAt(0).toUpperCase() + kind.slice(1);
}

export default function AnnotationReviewList({ annotations, prefersLightMode, onSelectAnnotation }: AnnotationReviewListProps): JSX.Element {
  const [query, setQuery] = React.useState("");
  const [filter, setFilter] = React.useState<(typeof FILTERS)[number]["key"]>("all");

  const visibleAnnotations = React.useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return annotations.filter((annotation) => {
      if (filter !== "all" && annotation.type !== filter) return false;
      if (!normalizedQuery) return true;

      const haystack = [
        annotation.rawNote,
        annotation.summary,
        annotation.subtype,
        annotation.severity,
        annotation.proposedAction,
        annotation.nextAction,
        annotation.action,
        annotation.assetLabel,
        annotation.anchorTimecode,
        annotation.frameIndex.toString(),
        annotation.source,
        annotation.type,
      ]
        .join(" ")
        .toLowerCase();

      return haystack.includes(normalizedQuery);
    });
  }, [annotations, filter, query]);

  return (
    <section className="mt-4 rounded-[18px] border border-white/10 bg-slate-950/20 px-3 py-3">
      <div className={`text-[10px] font-black uppercase tracking-[0.2em] ${prefersLightMode ? "text-slate-700/72" : "text-cyan-50/68"}`}>
        Annotation Review
      </div>
      <div className={`mt-2 text-[12px] ${prefersLightMode ? "text-slate-700/88" : "text-white/70"}`}>
        Search recent editorial notes before publish handoff.
      </div>

      <input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search notes, assets, timecode, or frame"
        className={`mt-3 w-full rounded-xl border px-3 py-2 text-[12px] outline-none ${
          prefersLightMode
            ? "border-slate-300/50 bg-white/72 text-slate-900 placeholder:text-slate-500/70"
            : "border-white/12 bg-slate-950/44 text-white/92 placeholder:text-white/36"
        }`}
      />

      <div className="mt-3 flex flex-wrap gap-2">
        {FILTERS.map((option) => {
          const active = option.key === filter;
          return (
            <button
              key={option.key}
              type="button"
              onClick={() => setFilter(option.key)}
              className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold ${
                active
                  ? prefersLightMode
                    ? "border-cyan-500/65 bg-cyan-100 text-cyan-900"
                    : "border-cyan-300/55 bg-cyan-300/18 text-cyan-50"
                  : prefersLightMode
                    ? "border-slate-300/55 bg-white/68 text-slate-700"
                    : "border-white/12 bg-white/6 text-white/72"
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      {visibleAnnotations.length === 0 ? (
        <div className={`mt-3 rounded-xl border border-dashed px-3 py-4 text-[12px] ${prefersLightMode ? "border-slate-300/55 text-slate-600" : "border-white/12 text-white/56"}`}>
          No matching annotations yet.
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          {visibleAnnotations.map((annotation) => (
            <article
              key={annotation.id}
              className={`rounded-xl border px-3 py-2 ${prefersLightMode ? "border-slate-300/45 bg-white/70" : "border-white/10 bg-white/6"}`}
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${prefersLightMode ? "bg-cyan-100 text-cyan-900" : "bg-cyan-300/18 text-cyan-50"}`}>
                  {typeLabel(annotation.type)}
                </span>
                <span className={`text-[11px] ${prefersLightMode ? "text-slate-600" : "text-white/58"}`}>
                  {annotation.anchorTimecode} · Frame {annotation.frameIndex.toLocaleString()}
                </span>
                <span className={`text-[11px] ${prefersLightMode ? "text-slate-600" : "text-white/58"}`}>
                  {annotation.source}
                </span>
              </div>
              <div className={`mt-2 text-[12px] leading-5 ${prefersLightMode ? "text-slate-900" : "text-white/86"}`}>
                {annotation.summary || annotation.rawNote}
              </div>
              {annotation.subtype || annotation.severity ? (
                <div className={`mt-2 flex flex-wrap gap-2 text-[10px] ${prefersLightMode ? "text-slate-600" : "text-white/58"}`}>
                  {annotation.subtype ? <span className="rounded-full border border-white/10 px-2 py-0.5">{annotation.subtype}</span> : null}
                  {annotation.severity ? <span className="rounded-full border border-white/10 px-2 py-0.5 uppercase">{annotation.severity}</span> : null}
                </div>
              ) : null}
              {annotation.nextAction || annotation.action ? (
                <div className={`mt-2 text-[11px] leading-5 ${prefersLightMode ? "text-cyan-900/88" : "text-cyan-50/78"}`}>
                  Action: {annotation.nextAction ?? annotation.action}
                </div>
              ) : null}
              {annotation.proposedAction ? (
                <div className={`mt-2 text-[11px] leading-5 ${prefersLightMode ? "text-slate-700/80" : "text-white/70"}`}>
                  Proposed action: {annotation.proposedAction}
                </div>
              ) : null}
              <div className={`mt-2 text-[11px] ${prefersLightMode ? "text-slate-600" : "text-white/58"}`}>
                {annotation.assetLabel}
              </div>
              {onSelectAnnotation ? (
                <div className="mt-3 flex justify-end">
                  <button
                    type="button"
                    onClick={() => onSelectAnnotation(annotation)}
                    className={`rounded-full border px-3 py-1 text-[10px] font-semibold ${
                      prefersLightMode
                        ? "border-cyan-400/55 bg-cyan-50 text-cyan-900 hover:bg-cyan-100"
                        : "border-cyan-300/24 bg-cyan-300/12 text-cyan-50 hover:bg-cyan-300/18"
                    }`}
                  >
                    Edit In Surface
                  </button>
                </div>
              ) : null}
            </article>
          ))}
        </div>
      )}
    </section>
  );
}